/**
 * ATM Menu Navigation System for Shopify
 * 
 * This script manages navigation between ATM screens for different ATM types.
 * Designed to be embedded in Shopify themes.
 */

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', function() {
  // Initialize ATM Navigation
  const ATMNavigation = (function() {
    // DOM Elements with namespaced IDs
    const domElements = {
      mainStage: document.querySelector('#atm-mainStage'),
      groupHeader: document.querySelector('#atm-group'),
      contentArea: document.querySelector('#atm-content'),
      selectionScreen: document.querySelector('#atm-firstSelectStage'),
      loader: document.getElementById('atm-loader'),
      progressBar: document.getElementById('atm-progress-bar'),
      progressText: document.getElementById('atm-progress-text'),
      genmegaButton: document.getElementById('atm-genmegaHantle'),
      hyosungButton: document.getElementById('atm-hyosung'),
      homeBtn: document.getElementById('atm-homeBtn')
    };

    // Application State
    const appState = {
      currentPageTitle: '01 - Error Code Screen',
      imageCache: {}, // Cache to store preloaded images
      baseImageUrl: "", // Base Shopify asset URL
      atmType: 'GENMEGA/HANTLE',
      loadingProgress: 0,
      pagesData: null // Will hold ATM page data
    };

    /**
     * Updates the loading progress bar
     * @param {number} percent - Current loading percentage (0-100)
     */
    function updateProgressBar(percent) {
      // Ensure percent is between 0 and 100
      const boundedPercent = Math.min(Math.max(percent, 0), 100);
      
      // Update the progress bar width and text
      domElements.progressBar.style.width = `${boundedPercent}%`;
      domElements.progressText.textContent = `${Math.round(boundedPercent)}%`;
      
      // Store the current progress
      appState.loadingProgress = boundedPercent;
    }

    /**
     * Loads the ATM page data
     * @returns {Promise} - Resolves when data is loaded
     */
    function loadATMData() {
      const dataUrl = "//atmtrader.com/cdn/shop/t/81/assets/atm-menu-navigation-data.json?v=28495272895973013301776651790";
      
      // Try to get data from a hidden field first (for Liquid-populated data)
      const dataField = document.querySelector('script[type="application/json"][data-atm-data]');
      if (dataField && dataField.textContent) {
        try {
          appState.pagesData = JSON.parse(dataField.textContent);
          return Promise.resolve(appState.pagesData);
        } catch (error) {
          console.error('Error parsing embedded ATM data:', error);
          // Continue with fetch if parsing fails
        }
      }
      
      // Fetch data from JSON file
      return fetch(dataUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to load ATM data');
          }
          return response.json();
        })
        .then(data => {
          appState.pagesData = data;
          return data;
        })
        .catch(error => {
          console.error('Error loading ATM data:', error);
          return null;
        });
    }

    /**
     * Creates a properly formatted Shopify asset URL
     * @param {string} filename - The asset filename
     * @returns {string} - The complete URL
     */
    function getAssetUrl(filename) {
      // If the filename already has a complete URL or CDN path, use it directly
      if (filename.startsWith('http') || filename.startsWith('//')) {
        return filename;
      }
      
      const baseUrl = "//atmtrader.com/cdn/shop/t/81/assets/empty";
      console.log('Base URL:', baseUrl);
      return "https:" + baseUrl.replace('empty', '') + filename;
    }

    /**
     * Preloads all images for the selected ATM type using lazy loading approach
     */
    function preloadImages() {
      if (!appState.pagesData) {
        console.error('No ATM data available for preloading images');
        updateProgressBar(100);
        domElements.loader.style.display = 'none';
        return;
      }

      const atmPages = appState.pagesData[appState.atmType];
      if (!atmPages || !atmPages.length) {
        console.error(`No pages found for ATM type: ${appState.atmType}`);
        updateProgressBar(100);
        domElements.loader.style.display = 'none';
        return;
      }

      let loadedCount = 0;
      const totalImages = atmPages.length;
      updateProgressBar(0); // Initialize progress bar

      // Process each ATM page
      atmPages.forEach(page => {
        const img = new Image();
        
        // Add data-src for lazy loading (as per Shopify pattern)
        const imgUrl = getAssetUrl(page.url);
        
        // Load the image
        img.src = imgUrl;

        img.setAttribute('data-src', imgUrl);
        img.classList.add('lazyload');
        
        // Add to image cache
        appState.imageCache[page.title] = img;

        loadedCount += 1;
        // Set up load and error handlers
        img.onload = () => handleImageLoaded(img, page.title, loadedCount, totalImages);
        img.onerror = () => handleImageError(page.url, loadedCount, totalImages);
      });
      
      if (totalImages === 0) {
        // No images to load, hide loader after a brief delay
        setTimeout(() => {
          domElements.loader.style.display = 'none';
        }, 500);
      }
    }
    
    /**
     * Handle image loaded event
     */
    function handleImageLoaded(img, title, loadedCount, totalImages) {
      // Store in cache if not already there
      if (!appState.imageCache[title]) {
        appState.imageCache[title] = img;
      }
      
      // Update progress
      const percentComplete = (loadedCount / totalImages) * 100;
      updateProgressBar(percentComplete);
      
      // If all images loaded, hide loader
      if (loadedCount >= totalImages) {
        setTimeout(() => {
          domElements.loader.style.display = 'none';
        }, 500);
      }
    }
    
    /**
     * Handle image loading error
     */
    function handleImageError(url, loadedCount, totalImages) {
      console.error(`Failed to load image: ${url}`);
      
      // Update progress even for failed images
      const percentComplete = (loadedCount / totalImages) * 100;
      updateProgressBar(percentComplete);
      
      // If all images attempted, hide loader
      if (loadedCount >= totalImages) {
        setTimeout(() => {
          domElements.loader.style.display = 'none';
        }, 500);
      }
    }

    /**
     * Finds a page by its title
     * @param {string} title - Page title to find
     * @returns {Object|null} - The found page or null
     */
    function findPage(title) {
      if (!appState.pagesData) return null;
      
      appState.currentPageTitle = title;
      return appState.pagesData[appState.atmType].find(page => page.title === title) || null;
    }

    /**
     * Displays a page by its title
     * @param {string} pageTitle - Title of the page to display
     */
    function displayPage(pageTitle) {
      const page = findPage(pageTitle);
      if (!page) {
        console.error(`Page not found: ${pageTitle}`);
        return;
      }

      // Clear previous content
      domElements.mainStage.innerHTML = '';
      domElements.groupHeader.innerHTML = '';
      domElements.contentArea.innerHTML = '';

      // Display page image
      const img = appState.imageCache[page.title];
      if (img) {
        domElements.mainStage.appendChild(img);
      } else {
        console.warn(`Image not found in cache for page: ${page.title}`);
        // Create a new image if not found in cache
        const newImg = new Image();
        newImg.src = getAssetUrl(page.url);
        newImg.onload = () => {
          appState.imageCache[page.title] = newImg;
        };
        newImg.onerror = () => {
          console.error(`Failed to load image for page: ${page.title}`);
        };
        domElements.mainStage.appendChild(newImg);
      }

      // Set group header if available
      if (page.group) {
        domElements.groupHeader.innerHTML = page.group;
      }

      // Create interactive buttons
      if (page.btns && page.btns.length) {
        createPageButtons(page.btns);
      }

      // Set content if available
      if (page.content) {
        domElements.contentArea.innerHTML = page.content;
      }

      // Add content image if available
      if (page.contentImg) {
        addContentImage(page.contentImg);
      }

      // Setup event listeners for navigation
      setupEventListeners();
    }

    /**
     * Creates interactive buttons on the page
     * @param {Array} buttons - Button configuration objects
     */
    function createPageButtons(buttons) {
      buttons.forEach(btn => {
        const button = document.createElement('div');
        button.style.position = 'absolute';
        button.style.left = btn.left + '%';
        button.style.top = btn.top + '%';
        button.style.width = btn.width + '%';
        button.style.height = btn.height + '%';
        // Uncomment for debugging buttons
        // button.style.backgroundColor = 'rgba(255, 0, 0, 0.2)';
        button.style.zIndex = '1';
        button.style.cursor = 'pointer';
        button.addEventListener('click', () => displayPage(btn.to));
        domElements.mainStage.appendChild(button);
      });
    }

    /**
     * Adds content image to the page
     * @param {string} contentImgUrl - URL of the content image
     */
    function addContentImage(contentImgUrl) {
      const contentImg = new Image();
      contentImg.src = getAssetUrl(contentImgUrl);
      contentImg.style.width = "300px";
      contentImg.onerror = () => console.error(`Failed to load content image: ${contentImgUrl}`);
      domElements.contentArea.appendChild(contentImg);
    }

    /**
     * Sets up event listeners for keyboard and mouse navigation
     */
    function setupEventListeners() {
      // Remove existing listeners to prevent duplication
      document.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('click', handleClick);
      
      // Add new listeners
      document.addEventListener('keydown', handleKeyPress);
      document.addEventListener('click', handleClick);
    }

    /**
     * Handles keyboard navigation
     * @param {KeyboardEvent} event - Keyboard event
     */
    function handleKeyPress(event) {
      if (!appState.pagesData) return;
      
      const currentPage = appState.pagesData[appState.atmType].find(page => page.title === appState.currentPageTitle);
      if (!currentPage?.keys) return;

      const keyAction = currentPage.keys.find(k => 
        k.key.toLowerCase() === event.key.toLowerCase()   
      );

      if (keyAction) {
        displayPage(keyAction.to);
      }
    }

    /**
     * Handles click events for pages with click navigation
     */
    function handleClick() {
      if (!appState.pagesData) return;
      
      const currentPage = appState.pagesData[appState.atmType].find(page => page.title === appState.currentPageTitle);
      if (!currentPage?.click) return;
      displayPage(currentPage.click);
    }

    /**
     * Navigates to home screen
     */
    function goHome() {
      appState.currentPageTitle = 'OF - Operator Function';
      displayPage(appState.currentPageTitle);
    }

    /**
     * Initializes the application
     */
    function initializeApp() {
      // Show loader and reset progress
      domElements.loader.style.display = 'flex';
      updateProgressBar(0);
      
      // Load ATM data, then preload images
      loadATMData().then(() => {
        preloadImages();
      });
      
      // Set up ATM selection buttons
      domElements.genmegaButton.addEventListener('click', () => {
        appState.atmType = 'GENMEGA/HANTLE';
        domElements.selectionScreen.style.display = 'none';
        domElements.mainStage.style.display = 'flex';
        domElements.groupHeader.style.display = 'flex';
        domElements.contentArea.style.display = 'block';
        domElements.homeBtn.style.display = 'block';
        
        displayPage(appState.currentPageTitle);
      });
      
      domElements.hyosungButton.addEventListener('click', () => {
        appState.atmType = 'GENMEGA/HANTLE'; // Currently using same data for both
        domElements.selectionScreen.style.display = 'none';
        domElements.mainStage.style.display = 'flex';
        domElements.groupHeader.style.display = 'flex';
        domElements.contentArea.style.display = 'block';
        domElements.homeBtn.style.display = 'block';
        
        displayPage(appState.currentPageTitle);
      });

      // Set up home button
      domElements.homeBtn.addEventListener('click', goHome);
    }

    // Export public methods
    return {
      init: initializeApp
    };
  })();

  // Initialize the application
  ATMNavigation.init();
}); 